// @ts-check
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// admin-frontend has no @tailwindcss/typography plugin (frontend-only per repo convention),
// so headings/lists/tables are styled explicitly here instead of via `prose` classes —
// approximating the customer-facing CmsContentPage.tsx rendering closely enough to catch
// formatting mistakes, without needing the two apps to share a typography setup.
const mdComponents = {
  h1: ({ children, ...p }) => (
    <h1 className="text-2xl font-serif mb-4 mt-6 text-gray-900" {...p}>
      {children}
    </h1>
  ),
  h2: ({ children, ...p }) => (
    <h2 className="text-xl font-serif mb-3 mt-6 text-gray-900" {...p}>
      {children}
    </h2>
  ),
  h3: ({ children, ...p }) => (
    <h3 className="text-lg font-medium mb-2 mt-4 text-gray-900" {...p}>
      {children}
    </h3>
  ),
  p: (p) => <p className="text-sm leading-relaxed text-gray-700 mb-4" {...p} />,
  ul: (p) => <ul className="list-disc list-inside text-sm text-gray-700 mb-4 space-y-1" {...p} />,
  ol: (p) => <ol className="list-decimal list-inside text-sm text-gray-700 mb-4 space-y-1" {...p} />,
  a: ({ children, ...p }) => (
    <a className="text-black underline hover:text-gray-600" target="_blank" rel="noopener noreferrer" {...p}>
      {children}
    </a>
  ),
  strong: (p) => <strong className="font-semibold text-gray-900" {...p} />,
  em: (p) => <em className="italic" {...p} />,
  blockquote: (p) => <blockquote className="border-l-2 border-gray-300 pl-4 italic text-gray-600 mb-4" {...p} />,
  table: (p) => (
    <div className="overflow-x-auto mb-4">
      <table className="w-full text-sm border-collapse" {...p} />
    </div>
  ),
  th: (p) => <th className="border rounded-lg border-gray-200 bg-gray-50 px-3 py-2 text-left font-medium" {...p} />,
  td: (p) => <td className="border rounded-lg border-gray-200 px-3 py-2" {...p} />,
  hr: () => <hr className="my-6 border-gray-200" />,
};

/**
 * Renders CMS markdown content the way the customer-facing page will, so an admin
 * can catch broken formatting or unresolved placeholders before saving.
 * @param {{ title?: string, content: string }} props
 */
const MarkdownPreview = ({ title, content }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-8 max-w-3xl mx-auto">
    {title && <h1 className="text-2xl sm:text-3xl font-serif mb-6 text-gray-900">{title}</h1>}
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
      {content?.trim() ? content : '_Nothing written yet._'}
    </ReactMarkdown>
  </div>
);

export default MarkdownPreview;
